import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { FloorPlan } from "@/types/FloorPlan.types";
import * as floorPlanService from "@/services/floorPlanService";

export const useFloorPlans = () => {
  const [floorPlans, setFloorPlans] = useState<FloorPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingName, setEditingName] = useState<string | null>(null);
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const fetchFloorPlans = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const plans = await floorPlanService.fetchUserFloorPlans(user.id);
      console.log('Fetched floor plans:', plans); // Debug log
      setFloorPlans(plans);
    } catch (error: any) {
      console.error("Error fetching floor plans:", error);
      toast({
        title: "Fout bij ophalen",
        description: "Er is een fout opgetreden bij het ophalen van je plattegronden.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) {
      navigate("/auth/login");
      return;
    }

    fetchFloorPlans();

    const channel = supabase
      .channel('floor_plans_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'floor_plans',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('Floor plan change detected:', payload); // Debug log
          fetchFloorPlans();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, navigate]);

  const handleUpdateName = async (id: string, newName: string) => {
    try {
      await floorPlanService.updateFloorPlanName(id, user?.id || '', newName);
      setFloorPlans(plans =>
        plans.map(plan =>
          plan.id === id ? { ...plan, name: newName } : plan
        )
      );
      setEditingName(null);
    } catch (error) {
      console.error("Error updating floor plan name:", error);
      toast({
        title: "Fout bij opslaan",
        description: "Er is een fout opgetreden bij het opslaan van de nieuwe naam.",
        variant: "destructive",
      });
    }
  };

  const handleDuplicate = async (plan: FloorPlan) => {
    try {
      await floorPlanService.duplicateFloorPlan(plan, user?.id || '');
      fetchFloorPlans();
      toast({
        title: "Plattegrond gekopieerd",
        description: "De plattegrond is succesvol gekopieerd.",
      });
    } catch (error) {
      console.error("Error duplicating floor plan:", error);
      toast({
        title: "Fout bij kopiëren",
        description: "Er is een fout opgetreden bij het kopiëren van de plattegrond.",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await floorPlanService.deleteFloorPlan(id, user?.id || '');
      setFloorPlans(plans => plans.filter(plan => plan.id !== id));
      toast({
        title: "Plattegrond verwijderd",
        description: "De plattegrond is succesvol verwijderd.",
      });
    } catch (error) {
      console.error("Error deleting floor plan:", error);
      toast({
        title: "Fout bij verwijderen",
        description: "Er is een fout opgetreden bij het verwijderen van de plattegrond.",
        variant: "destructive",
      });
    }
  };

  const handleUpdateFolder = async (id: string, folderId: string | null) => {
    try {
      await floorPlanService.updateFloorPlanFolder(id, user?.id || '', folderId);
      setFloorPlans(plans =>
        plans.map(plan =>
          plan.id === id ? { ...plan, folder_id: folderId } : plan
        )
      );
      toast({
        title: "Map bijgewerkt",
        description: "De plattegrond is succesvol verplaatst.",
      });
    } catch (error) {
      console.error("Error updating folder:", error);
      toast({
        title: "Fout bij verplaatsen",
        description: "Er is een fout opgetreden bij het verplaatsen van de plattegrond.",
        variant: "destructive",
      });
    }
  };

  const handleToggleFavorite = async (id: string, favorite: boolean) => {
    try {
      await floorPlanService.updateFloorPlanFavorite(id, user?.id || '', favorite);
      setFloorPlans(plans =>
        plans.map(plan =>
          plan.id === id ? { ...plan, is_favorite: favorite } : plan
        )
      );
    } catch (error) {
      console.error("Error toggling favorite:", error);
      toast({
        title: "Fout bij favoriet maken",
        description: "Er is een fout opgetreden bij het wijzigen van de favoriet status.",
        variant: "destructive",
      });
    }
  };

  const handleToggleVisibility = async (id: string, isPublic: boolean) => {
    try {
      await floorPlanService.updateFloorPlanVisibility(id, user?.id || '', isPublic);
      setFloorPlans(plans =>
        plans.map(plan =>
          plan.id === id ? { ...plan, is_public: isPublic } : plan
        )
      );
      toast({
        title: isPublic ? "Plattegrond openbaar gemaakt" : "Plattegrond privé gemaakt",
        description: isPublic
          ? "De plattegrond is nu zichtbaar voor andere gebruikers."
          : "De plattegrond is nu alleen zichtbaar voor jou.",
      });
    } catch (error) {
      console.error("Error toggling visibility:", error);
      toast({
        title: "Fout bij wijzigen zichtbaarheid",
        description: "Er is een fout opgetreden bij het wijzigen van de zichtbaarheid.",
        variant: "destructive",
      });
    }
  };

  return {
    floorPlans,
    loading,
    editingName,
    setEditingName,
    handleUpdateName,
    handleDuplicate,
    handleDelete,
    handleUpdateFolder,
    handleToggleFavorite,
    handleToggleVisibility,
  };
};
